import {
    DeviceTypeConfigurations,
    FirstPartyDeviceType,
    doesDeviceVersionMatchSelector,
    isDeviceVersionSelectorValid,
} from "verdi-common";
import { DeviceGeneration } from "verdi-common/DeviceConfigs";

import type DeviceMapEntity from "../map-management/map-entities/device-map-entity";

/**
 * The generation code for the device (numbers)
 */
export enum GENERATION_CODE {
    SEED = "1",
    SAPLING = "2",
    SPROUT = "3",
    GEN4 = "4",
}

/**
 * The product line code (all lowercase)
 */
export enum PRODUCT_LINE_CODE {
    BLOCK = "block",
    MICRO = "micro",
    UNKNOWN = "?",
}

interface ComposeDeviceVersionCodeOptions {
    generation?: GENERATION_CODE | "*";
    productLine?: PRODUCT_LINE_CODE | "*";
    hardwareVersion?: string;
    hardwareVariant?: string;
    firmwareVersion?: string;
}

// Legacy support to convert the DeviceConfig generation string to the generation code
export const generationToGenerationCode: Record<DeviceGeneration, GENERATION_CODE> = {
    seed: GENERATION_CODE.SEED,
    sap: GENERATION_CODE.SAPLING,
    sprout: GENERATION_CODE.SPROUT,
    gen4: GENERATION_CODE.GEN4,
};

export const composeDeviceVersionCode = ({
    generation = "*",
    productLine = "*",
    hardwareVersion = "*",
    hardwareVariant = "*",
    firmwareVersion = "*",
}: ComposeDeviceVersionCodeOptions) =>
    `${generation}.${productLine}.${hardwareVersion}.${hardwareVariant}.${firmwareVersion}`;

export const doesDeviceVersionMatch = (
    model: DeviceMapEntity["model"],
    {
        generation = "*",
        productLine = "*",
        hardwareVersion = "*",
        hardwareVariant = "*",
        firmwareVersion = "*",
    }: ComposeDeviceVersionCodeOptions,
) => {
    const versionCode = composeDeviceVersionCode({
        generation,
        productLine,
        hardwareVersion,
        hardwareVariant,
        firmwareVersion,
    });

    if (model?.deviceVersionInfo && isDeviceVersionSelectorValid(versionCode)) {
        return doesDeviceVersionMatchSelector(model?.deviceVersionInfo, versionCode);
    }

    return false;
};

export const isBlockDevice = (model: DeviceMapEntity["model"]): boolean =>
    model?.deviceVersionInfo
        ? doesDeviceVersionMatchSelector(model.deviceVersionInfo, "(3.block.*.*.*) (4.block.*.*.*)")
        : false;

/**
 * The legacy generation string of the device (different from the generation code)
 */
export function getDeviceGeneration(deviceType: FirstPartyDeviceType): DeviceGeneration {
    const deviceConfig = DeviceTypeConfigurations[deviceType];

    if (deviceConfig?.isVerdiDevice !== true) {
        throw new Error(`Invalid device type: ${deviceType}`);
    }

    return deviceConfig.generation;
}
